import * as React from "react"
import { useState, useEffect } from "react"
import { cn } from "../../lib/utils"
import { Sidebar } from "./Sidebar"
import { TopBar } from "./TopBar"
import { AppStep } from "../../src/types"

interface BreadcrumbItem {
  label: string
  onClick?: () => void
}

interface BackButtonConfig {
  label: string
  action: () => void
}

interface DashboardLayoutProps {
  children: React.ReactNode
  currentStep: AppStep
  onNavigate: (step: AppStep) => void
  isAdmin: boolean
  creditBalance?: number
  userEmail?: string
  onSignOut: () => void
  onBuyCredits?: () => void
  breadcrumbs?: BreadcrumbItem[]
  showSearch?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  topBarActions?: React.ReactNode
  backButton?: BackButtonConfig | null
}

export function DashboardLayout({
  children,
  currentStep,
  onNavigate,
  isAdmin,
  creditBalance,
  userEmail,
  onSignOut,
  onBuyCredits,
  breadcrumbs,
  showSearch,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  topBarActions,
  backButton
}: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [currentStep])

  // Load sidebar collapse state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    if (saved !== null) {
      setSidebarCollapsed(saved === 'true')
    }
  }, [])

  // Save sidebar collapse state to localStorage
  const handleToggleCollapse = () => {
    const newValue = !sidebarCollapsed
    setSidebarCollapsed(newValue)
    localStorage.setItem('sidebar-collapsed', String(newValue))
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        currentStep={currentStep}
        onNavigate={onNavigate}
        isAdmin={isAdmin}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        isMobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        creditBalance={creditBalance}
        userEmail={userEmail}
        onSignOut={onSignOut}
      />

      {/* Main Content Area */}
      <div
        className={cn(
          "flex flex-col min-h-screen transition-all duration-200",
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"
        )}
      >
        {/* Top Bar */}
        <TopBar
          breadcrumbs={breadcrumbs}
          onSidebarToggle={() => setMobileMenuOpen(true)}
          creditBalance={creditBalance}
          onBuyCredits={onBuyCredits}
          onViewAccount={() => onNavigate(AppStep.ACCOUNT)}
          showSearch={showSearch}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          actions={topBarActions}
          backButton={backButton}
        />

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}

// Container component for consistent max-width and padding
interface ContentContainerProps {
  children: React.ReactNode
  className?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | '7xl' | 'full'
}

export function ContentContainer({ 
  children, 
  className,
  maxWidth = '7xl'
}: ContentContainerProps) {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full'
  }

  return (
    <div className={cn("mx-auto w-full", maxWidthClasses[maxWidth], className)}>
      {children}
    </div>
  )
}

// Grid layout for dashboard cards
interface DashboardGridProps {
  children: React.ReactNode
  columns?: 1 | 2 | 3 | 4
  className?: string
}

export function DashboardGrid({ 
  children, 
  columns = 3,
  className 
}: DashboardGridProps) {
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  }

  return (
    <div className={cn("grid gap-4 lg:gap-6", columnClasses[columns], className)}>
      {children}
    </div>
  )
}

// Stat card for dashboard metrics
interface StatCardProps {
  title: string
  value: string | number
  description?: string
  icon?: React.ReactNode
  trend?: {
    value: number
    label: string
    positive?: boolean
  }
  className?: string
}

export function StatCard({
  title,
  value,
  description,
  icon,
  trend,
  className
}: StatCardProps) {
  return (
    <div className={cn(
      "bg-card rounded-xl border border-border p-6 shadow-sm",
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {icon && (
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            {icon}
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-4 flex items-center gap-1 text-sm">
          <span className={cn(
            "font-medium",
            trend.positive ? "text-green-600" : "text-red-600"
          )}>
            {trend.positive ? "+" : ""}{trend.value}%
          </span>
          <span className="text-muted-foreground">{trend.label}</span>
        </div>
      )}
    </div>
  )
}
